/** Langue d'affichage client normalisée (UI + données FR / DE / EN / IT). */

export type ClientDisplayLang = 'fr' | 'de' | 'en' | 'it';



/** Langue de contenu produit / commerce (nom, description). */

export type ContentLang = ClientDisplayLang;



export type LangLabels<T> = { fr: T; de: T; en: T; it?: T };



/** Chaîne UI selon la langue i18n active (fr / de / en / it). */

/** Libellé UI 4 langues (it requis pour un texte italien dédié). */
export function uiL<T>(raw: string, fr: T, de: T, en: T, it: T): T {
  return pickLang(raw, { fr, de, en, it });
}

export function pickLang<T>(raw: string, labels: LangLabels<T> & { fr: T; de: T; en: T }): T {

  if (raw.startsWith('it')) return labels.it ?? labels.en;

  if (raw.startsWith('de')) return labels.de;

  if (raw.startsWith('en')) return labels.en;

  return labels.fr;

}



export function resolveClientLang(raw: string): ClientDisplayLang {

  if (raw.startsWith('it')) return 'it';

  if (raw.startsWith('de')) return 'de';

  if (raw.startsWith('en')) return 'en';

  return 'fr';

}



type LocalizedNames = {

  nameFr: string;

  nameDe?: string | null;

  nameEn?: string | null;

  nameIt?: string | null;

};



type LocalizedDescriptions = {

  descriptionFr?: string | null;

  descriptionDe?: string | null;

  descriptionEn?: string | null;

  descriptionIt?: string | null;

};



export function shopTitle(shop: LocalizedNames, lang: ClientDisplayLang): string {

  if (lang === 'de') return shop.nameDe ?? shop.nameFr;

  if (lang === 'en') return shop.nameEn ?? shop.nameFr;

  if (lang === 'it') return shop.nameIt ?? shop.nameEn ?? shop.nameFr;

  return shop.nameFr;

}



export function shopDescription(shop: LocalizedDescriptions, lang: ClientDisplayLang): string | undefined {

  if (lang === 'de') return shop.descriptionDe ?? shop.descriptionFr ?? undefined;

  if (lang === 'en') return shop.descriptionEn ?? shop.descriptionFr ?? undefined;

  if (lang === 'it') return shop.descriptionIt ?? shop.descriptionEn ?? shop.descriptionFr ?? undefined;

  return shop.descriptionFr ?? undefined;

}



export function productTitle(p: LocalizedNames, lang: ClientDisplayLang): string {

  if (lang === 'de') return p.nameDe ?? p.nameFr;

  if (lang === 'en') return p.nameEn ?? p.nameFr;

  if (lang === 'it') return p.nameIt ?? p.nameEn ?? p.nameFr;

  return p.nameFr;

}



export function productDescription(p: LocalizedDescriptions, lang: ClientDisplayLang): string | undefined {

  if (lang === 'de') return p.descriptionDe ?? p.descriptionFr ?? undefined;

  if (lang === 'en') return p.descriptionEn ?? p.descriptionFr ?? undefined;

  if (lang === 'it') return p.descriptionIt ?? p.descriptionEn ?? p.descriptionFr ?? undefined;

  return p.descriptionFr ?? undefined;

}



export function categoryLabel(

  c: LocalizedNames,

  lang: ClientDisplayLang,

): string {

  if (lang === 'de') return c.nameDe ?? c.nameFr;

  if (lang === 'en') return c.nameEn ?? c.nameFr;

  if (lang === 'it') return c.nameIt ?? c.nameEn ?? c.nameFr;

  return c.nameFr;

}



type LocalizedPromotions = {

  promotionFr?: string | null;

  promotionDe?: string | null;

  promotionEn?: string | null;

  promotionIt?: string | null;

};



export function promotionLine(shop: LocalizedPromotions, lang: ClientDisplayLang): string | null {
  const line = pickLang(lang, {
    fr: shop.promotionFr ?? '',
    de: shop.promotionDe ?? shop.promotionFr ?? '',
    en: shop.promotionEn ?? shop.promotionFr ?? '',
    it: shop.promotionIt ?? shop.promotionEn ?? shop.promotionFr ?? '',
  });
  return line.trim() ? line : null;
}



/** Libellé catégorie Home (chip). */

export function homeCategoryLabel(

  cat: { labelFr: string; labelDe: string; labelEn: string; labelIt?: string },

  lang: ClientDisplayLang,

): string {

  if (lang === 'de') return cat.labelDe;

  if (lang === 'en') return cat.labelEn;

  if (lang === 'it') return cat.labelIt ?? cat.labelEn;

  return cat.labelFr;

}


